/**
 * FilterPanel Component
 * Collapsible filter panel with automatic filter application
 */

import { debounce } from '../utils/helpers.js';

/**
 * FilterPanel class for managing collapsible filter panels
 */
export class FilterPanel {
  /**
   * Create a FilterPanel instance
   * @param {object} options - Configuration options
   * @param {string} options.panelId - ID of the filter panel element
   * @param {string} options.toggleBtnId - ID of the toggle button
   * @param {string} options.resetBtnId - ID of the reset button (optional)
   * @param {string} options.formId - ID of the filter form (optional)
   * @param {number} options.searchDebounce - Debounce time for search inputs (default: 300)
   * @param {boolean} options.autoApply - Auto-apply filters on change (default: true)
   * @param {Function} options.onApply - Callback when filters are applied
   * @param {Function} options.onReset - Callback when filters are reset
   * @param {Function} options.onToggle - Callback when panel is toggled
   */
  constructor(options) {
    this.panelId = options.panelId;
    this.toggleBtnId = options.toggleBtnId;
    this.resetBtnId = options.resetBtnId;
    this.formId = options.formId;
    this.searchDebounce = options.searchDebounce || 300;
    this.autoApply = options.autoApply !== false;
    this.onApply = options.onApply;
    this.onReset = options.onReset;
    this.onToggle = options.onToggle;

    // State
    this.isOpen = false;
    this.filters = {};

    // Elements
    this.panel = document.getElementById(this.panelId);
    this.toggleBtn = document.getElementById(this.toggleBtnId);
    this.resetBtn = this.resetBtnId ? document.getElementById(this.resetBtnId) : null;
    this.form = this.formId ? document.getElementById(this.formId) : this.panel?.querySelector('form');

    // Bind methods
    this.toggle = this.toggle.bind(this);
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.applyFilters = this.applyFilters.bind(this);
    this.resetFilters = this.resetFilters.bind(this);
    this.getFilters = this.getFilters.bind(this);

    // Initialize
    this.bindEvents();
    this.initializeFromUrl();
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Toggle button
    if (this.toggleBtn) {
      this.toggleBtn.addEventListener('click', this.toggle);
    }

    // Reset button
    if (this.resetBtn) {
      this.resetBtn.addEventListener('click', this.resetFilters);
    }

    // Auto-apply on form changes
    if (this.autoApply && this.panel) {
      // Select/checkbox changes
      this.panel.querySelectorAll('select, input[type="checkbox"], input[type="radio"]').forEach((el) => {
        el.addEventListener('change', this.applyFilters);
      });

      // Text input with debounce
      const debouncedApply = debounce(this.applyFilters, this.searchDebounce);
      this.panel.querySelectorAll('input[type="text"], input[type="search"]').forEach((el) => {
        el.addEventListener('input', debouncedApply);
        el.addEventListener('keypress', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.applyFilters();
          }
        });
      });
    }

    // Form submit
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.applyFilters();
      });
    }
  }

  /**
   * Initialize filter values from URL
   */
  initializeFromUrl() {
    const params = new URLSearchParams(window.location.search);
    params.forEach((value, key) => {
      this.filters[key] = value;
    });

    // Update active filter count badge
    this.updateBadge();
  }

  /**
   * Toggle panel visibility
   */
  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Open the panel
   */
  open() {
    if (!this.panel) return;

    this.panel.classList.remove('hidden');
    this.isOpen = true;

    // Update toggle button icon (rotate chevron)
    const chevron = this.toggleBtn?.querySelector('svg');
    if (chevron) {
      chevron.classList.add('rotate-180');
    }

    if (this.onToggle) {
      this.onToggle(true, this);
    }
  }

  /**
   * Close the panel
   */
  close() {
    if (!this.panel) return;

    this.panel.classList.add('hidden');
    this.isOpen = false;

    // Update toggle button icon
    const chevron = this.toggleBtn?.querySelector('svg');
    if (chevron) {
      chevron.classList.remove('rotate-180');
    }

    if (this.onToggle) {
      this.onToggle(false, this);
    }
  }

  /**
   * Get current filter values
   * @returns {object} Current filters
   */
  getFilters() {
    const filters = {};

    if (!this.panel) return filters;

    // Get all form inputs
    this.panel.querySelectorAll('select, input').forEach((el) => {
      const name = el.name;
      if (!name) return;

      if (el.type === 'checkbox' || el.type === 'radio') {
        if (el.checked) {
          filters[name] = el.value;
        }
      } else if (el.value.trim()) {
        filters[name] = el.value.trim();
      }
    });

    return filters;
  }

  /**
   * Apply current filters
   */
  applyFilters() {
    this.filters = this.getFilters();
    this.updateBadge();

    if (this.onApply) {
      this.onApply(this.filters, this);
    }
  }

  /**
   * Reset all filters
   */
  resetFilters() {
    if (!this.panel) return;

    // Reset form elements
    this.panel.querySelectorAll('select').forEach((el) => {
      el.value = '';
      // Update custom select display if present
      const customText = el.closest('.custom-select-wrapper')?.querySelector('.custom-select-text');
      if (customText && el.options[0]) {
        customText.textContent = el.options[0].text;
      }
    });

    this.panel.querySelectorAll('input[type="text"], input[type="search"]').forEach((el) => {
      el.value = '';
    });

    this.panel.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((el) => {
      el.checked = false;
    });

    // Clear state
    this.filters = {};
    this.updateBadge();

    if (this.onReset) {
      this.onReset(this);
    } else if (this.onApply) {
      this.onApply(this.filters, this);
    }
  }

  /**
   * Update filter count badge
   */
  updateBadge() {
    const badge = this.toggleBtn?.querySelector('.filter-badge, [data-filter-badge]');
    // Exclude pagination, sorting, UI state params, and default values from count
    const excludeKeys = ['page', 'sort', 'order', 'dir', 'show_filters', 'edit', 'view'];
    const count = Object.keys(this.filters).filter((k) => {
      if (!this.filters[k] || excludeKeys.includes(k)) return false;
      // Exclude range=today as it's the default
      if (k === 'range' && this.filters[k] === 'today') return false;
      return true;
    }).length;

    if (badge) {
      if (count > 0) {
        badge.textContent = count;
        badge.style.display = '';
        badge.className = 'filter-badge ml-1.5 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-primary-600 rounded-full';
      } else {
        badge.style.display = 'none';
        badge.className = 'filter-badge';
        badge.textContent = '';
      }
    }
  }

  /**
   * Set a specific filter value programmatically
   * @param {string} name - Filter name
   * @param {string} value - Filter value
   * @param {boolean} apply - Whether to apply immediately (default: true)
   */
  setFilter(name, value, apply = true) {
    // Update DOM element
    const el = this.panel?.querySelector(`[name="${name}"]`);
    if (el) {
      if (el.type === 'checkbox' || el.type === 'radio') {
        el.checked = value === el.value;
      } else {
        el.value = value;
      }
    }

    // Update state
    if (value) {
      this.filters[name] = value;
    } else {
      delete this.filters[name];
    }

    if (apply) {
      this.applyFilters();
    }
  }

  /**
   * Destroy the filter panel instance
   */
  destroy() {
    this.panel = null;
    this.toggleBtn = null;
    this.resetBtn = null;
    this.form = null;
  }
}

// Expose globally for backward compatibility
window.FilterPanel = FilterPanel;

export default FilterPanel;
